/**
 * FlipEffect creates a card flip animation where slides rotate as they move through the carousel.
 *
 * The effect works by rotating slides along the Y-axis as they transition through specific zones:
 * - L1 → CL1: Cards flip from back (-180°) to front (0°) as they enter the center
 * - CL1 → R1: Cards flip from front (0°) to back (180°) as they leave the center
 * - Beyond L1 or R1: Cards are completely hidden
 *
 * This creates a smooth card-flipping animation where adjacent slides seamlessly transition
 * into each other, simulating cards being flipped over as the carousel moves.
 */

// Factory function - class is created when TarotEffect is available
function createFlipEffect(TarotEffect) {
	return class FlipEffect extends TarotEffect {
		static effectName = 'flip';

		/**
		 * Effect configuration rules that define carousel behavior constraints
		 */
		static rules = {
			minSlidesPerView: 1,
			maxSlidesPerView: 3,
			loopBuffer: { left: 1, right: 1 },
		};

		/**
		 * Creates a new flip effect instance
		 * @param {Object} ctx - The shared context object
		 */
		constructor(ctx) {
			super(ctx);
			const _ = this;

			// 3D animation settings
			_.perspectiveDistance = 2000; // Distance for 3D perspective (higher = less distortion)
			_.maxRotationAngle = 180; // Maximum rotation angle for card flips

			_.init();
		}

		/**
		 * Initialize the flip effect by setting up 3D perspective and calculating reference points
		 */
		init() {
			super.init();
			this.setupViewport3D();
		}

		/**
		 * Reinitialize effect when carousel layout changes (resize, options update, etc.)
		 */
		reInit() {
			super.reInit();
			this.setupViewport3D();
		}

		/**
		 * Configure the viewport for 3D transformations by applying CSS perspective
		 * This enables the 3D flip effect to work properly
		 */
		setupViewport3D() {
			const _ = this;
			const viewport = _.ctx.viewport;

			// Apply 3D perspective to the viewport container
			viewport.style.perspective = `${_.perspectiveDistance}px`;
			viewport.style.perspectiveOrigin = 'center center';
		}

		/**
		 * Main render function called every animation frame to position and transform slides
		 * Uses the frame-based architecture with dependency injection for utilities
		 *
		 * @param {Object} frame - Complete frame object with all carousel data
		 * @param {Array} frame.slides - Sorted slides array with calculated positions
		 * @param {Object} frame.widths - Layout measurements
		 * @param {Object} frame.state - Carousel state
		 * @param {Object} frame.animation - Animation data including trackPosition
		 * @param {Object} frame.transformPoints - Named position points
		 * @param {Object} utils - Frame utilities for position calculations
		 * @param {Function} utils.isSlideInRange - Check if slide is in range with progress
		 */
		render(frame, utils) {
			const _ = this;
			const { slides, widths, animation } = frame;

			// Flip effect uses calculated slide width, track position is virtual only
			_.renderSlideWidth(widths.slide);
			// Note: renderTrackPosition() not called - track DOM position irrelevant for flip

			// Process each slide to determine its flip transformation
			for (let slideIndex = 0, totalSlides = slides.length; slideIndex < totalSlides; slideIndex++) {
				const slide = slides[slideIndex];

				// Reset slide to visible defaults before applying zone-specific transforms
				slide.show();
				slide.style.opacity = 1;
				slide.style.zIndex = 1;

				// Determine which transition zone this slide is currently in using frame utils
				// Note: frame utils already handle track position conversion internally
				const leftTransition = utils.isSlideInRange(slide, 'CL1', 'L1');
				// Apply transforms based on which transition zone the slide is in
				if (leftTransition.isInRange) {
					// L1 → CL1: Card flipping from back to front as it enters center
					_.applyLeftFlipTransition(slide, leftTransition.percent);
					continue;
				}

				const rightTransition = utils.isSlideInRange(slide, 'R1', 'CL1');
				if (rightTransition.isInRange) {
					// CL1 → R1: Card flipping from front to back as it leaves center
					_.applyRightFlipTransition(slide, rightTransition.percent);
					continue;
				}

				// Beyond visible range: Hide the slide completely
				_.applyHiddenState(slide);
			}
		}

		/**
		 * Apply flip transformation for slides in the left transition zone (L1 → CL1)
		 * Cards rotate from -180° (back) to 0° (front) as they move toward center
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {number} transitionPercent - Progress through transition (0 = at L1, 1 = at CL1)
		 */
		applyLeftFlipTransition(slide, transitionPercent) {
			// Calculate rotation: starts at -180° (back), ends at 0° (front)
			const rotationDegrees = -180 + transitionPercent * 180;

			slide.style.opacity = 1;
			slide.style.zIndex = 1;
			slide.style.transform = `rotateY(${rotationDegrees}deg)`;
		}

		/**
		 * Apply flip transformation for slides in the right transition zone (CL1 → R1)
		 * Cards rotate from 0° (front) to 180° (back) as they move away from center
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {number} transitionPercent - Progress through transition (0 = at CL1, 1 = at R1)
		 */
		applyRightFlipTransition(slide, transitionPercent) {
			// Calculate rotation: starts at 0° (front), ends at 180° (back)
			const rotationDegrees = transitionPercent * 180;

			slide.style.opacity = 1;
			slide.style.zIndex = 1;
			slide.style.transform = `rotateY(${rotationDegrees}deg)`;
		}

		/**
		 * Hide slides that are outside the visible flip transition zones
		 * This applies to slides beyond L1 (far left) or R1 (far right)
		 *
		 * @param {HTMLElement} slide - The slide element to hide
		 */
		applyHiddenState(slide) {
			slide.style.opacity = 0;
			slide.hide();
			slide.style.zIndex = 0;
			slide.style.transform = 'rotateY(0deg)'; // Reset rotation for hidden slides
		}

		/**
		 * Clean up the effect when it's being destroyed or replaced
		 * Removes 3D perspective and resets all slide transforms to default state
		 */
		destroy() {
			super.destroy();
			const _ = this;

			// Remove 3D perspective from viewport
			const viewport = _.ctx.viewport;
			viewport.style.perspective = '';
			viewport.style.perspectiveOrigin = '';

			// Reset all slides to default state
			// slides are now DOM elements directly, not wrapper objects
			const slides = _.ctx.store.getSlides();
			for (let i = 0; i < slides.length; i++) {
				const slide = slides[i];
				slide.style.transform = '';
				slide.style.opacity = '';
				slide.style.zIndex = '';
				slide.show?.();
			}
		}
	};
}

// Queue the factory (works regardless of whether Tarot is loaded)
(window.TarotEffectQueue = window.TarotEffectQueue || []).push({
	effectName: 'flip',
	factory: createFlipEffect
});

export default createFlipEffect;
